import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { fetchProductAnalytics } from "../../services/analyticsService";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import DatamapsIndia from "react-datamaps-india";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
  LabelList,
} from "recharts";
import { formatAmount } from "../../utils/formatters";

const stateNameMapping = {
  "andaman & nicobar": "Andaman & Nicobar Island",
  "andaman and nicobar": "Andaman & Nicobar Island",
  "andhra pradesh": "Andhra Pradesh",
  "arunachal pradesh": "Arunanchal Pradesh",
  assam: "Assam",
  bihar: "Bihar",
  chandigarh: "Chandigarh",
  chhattisgarh: "Chhattisgarh",
  delhi: "Delhi",
  goa: "Goa",
  gujarat: "Gujarat",
  haryana: "Haryana",
  "himachal pradesh": "Himachal Pradesh",
  "jammu & kashmir": "Jammu & Kashmir",
  "jammu and kashmir": "Jammu & Kashmir",
  jharkhand: "Jharkhand",
  karnataka: "Karnataka",
  kerala: "Kerala",
  lakshadweep: "Lakshadweep",
  "madhya pradesh": "Madhya Pradesh",
  maharashtra: "Maharashtra",
  manipur: "Manipur",
  meghalaya: "Meghalaya",
  mizoram: "Mizoram",
  nagaland: "Nagaland",
  odisha: "Odisha",
  orissa: "Odisha",
  puducherry: "Puducherry",
  punjab: "Punjab",
  rajasthan: "Rajasthan",
  sikkim: "Sikkim",
  "tamil nadu": "Tamil Nadu",
  telangana: "Telangana",
  tripura: "Tripura",
  "uttar pradesh": "Uttar Pradesh",
  uttarakhand: "Uttarakhand",
  "west bengal": "West Bengal",
};

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884D8"];


const ProductDetail = () => {
  const { productName: skuOrName } = useParams(); // from ProductAnalytics this is sku; from other pages may be product name
  const navigate = useNavigate();
  const location = useLocation();
  const useSku = location.state?.useSku === true;
  const displayName = location.state?.productName || skuOrName;

  // Get dates and filters from navigation state or use defaults
  const getInitialStartDate = () => {
    if (location.state?.startDate) {
      return new Date(location.state.startDate);
    }
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d;
  };

  const getInitialEndDate = () => {
    if (location.state?.endDate) {
      return new Date(location.state.endDate);
    }
    return new Date();
  };

  const [startDate, setStartDate] = useState(getInitialStartDate);
  const [endDate, setEndDate] = useState(getInitialEndDate);
  const [timeRange, setTimeRange] = useState(
    location.state?.timeRange || "month"
  );
  const [postingAccount, setPostingAccount] = useState(
    location.state?.postingAccount || ""
  );
  const [state, setState] = useState(location.state?.state || "");
  const [zone, setZone] = useState(location.state?.zone || "");
  const [showQuantity, setShowQuantity] = useState(
    location.state?.showQuantity || false
  );
  const [showAllProducts, setShowAllProducts] = useState(
    location.state?.showAllProducts || false
  );
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchProductAnalytics({
          startDate: startDate.toLocaleDateString("en-CA").slice(0, 10),
          endDate: endDate.toLocaleDateString("en-CA").slice(0, 10),
          ...(useSku ? { productSku: skuOrName } : { productName: skuOrName }),
        });
        setData(res);
      } catch (err) {
        setError("Failed to fetch product analytics");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [startDate, endDate, skuOrName, useSku]);

  // Prepare data for charts
  const mapData = (data?.stateWiseDistribution || []).reduce((acc, curr) => {
    const stateKey =
      stateNameMapping[(curr.state || "").toLowerCase()] || curr.state;
    acc[stateKey] = {
      value: showQuantity ? curr.qty : curr.net_amount,
      color: showQuantity
        ? `hsl(200, 70%, ${60 + curr.qty * 2}%)`
        : `hsl(220, 80%, ${60 + curr.net_amount_l * 100}%)`,
    };
    return acc;
  }, {});

  // Calculate date range difference
  const daysDifference = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
  
  let salesLineData;
  if (daysDifference > 30) {
    // Group data by month for date ranges > 30 days
    const monthlyData = {};
    
    (data?.salesLineGraph || []).forEach((item) => {
      const monthKey = new Date(item.date).toLocaleDateString("en-CA", {
        year: "numeric",
        month: "short"
      });
      const value = showQuantity ? item.qty : item.net_amount;
      
      if (!monthlyData[monthKey]) {
        monthlyData[monthKey] = {
          date: monthKey,
          value: 0,
        };
      }
      
      monthlyData[monthKey].value += value;
    });
    
    salesLineData = Object.values(monthlyData).sort((a, b) => {
      return new Date(a.date) - new Date(b.date);
    });
  } else {
    // Use daily data for date ranges <= 30 days
    salesLineData = (data?.salesLineGraph || []).map((item) => ({
      date: item.date,
      value: showQuantity ? item.qty : item.net_amount,
    }));
  }

  const zoneBarData = (data?.zoneWiseDistribution || []).map((item) => ({
    name: item.zone,
    value: showQuantity ? item.qty : item.net_amount,
  }));

  const platformBarData = (data?.postingAccountWiseDistribution || []).map(
    (item) => ({
      name: item.posting_account,
      value: showQuantity ? item.qty : item.net_amount,
    })
  );

  // Check if we should show data labels (only if 15 or fewer data points)
  const shouldShowSalesLabels = salesLineData.length <= 15;
  const shouldShowZoneLabels = zoneBarData.length <= 15;
  const shouldShowPlatformLabels = platformBarData.length <= 15;


  return (
    <div className="container mx-auto p-4">
      <button
        className="btn btn-ghost btn-sm mb-2"
        onClick={() => {
          // Check if we came from a specific page via navigation state
          if (location.state?.fromPage) {
            // Navigate back to the specific page with preserved dates and all filters
            const backState = {
              startDate: startDate.toLocaleDateString("en-CA"),
              endDate: endDate.toLocaleDateString("en-CA"),
            };

            // Add analytics-specific filters if coming from analytics pages
            if (location.state.fromPage.includes("/analytics/")) {
              backState.timeRange = timeRange;
              backState.postingAccount = postingAccount;
              backState.state = state;
              backState.zone = zone;
              backState.showQuantity = showQuantity;
              backState.showAllProducts = showAllProducts;
            }

            // Add orders-specific filters if coming from orders page
            if (location.state.fromPage === "/orders") {
              backState.postingAccount = location.state.postingAccount;
              backState.zone = location.state.zone;
              backState.status = location.state.status;
              backState.fetchType = location.state.fetchType;
              backState.searchValue = location.state.searchValue;
              backState.sortField = location.state.sortField;
              backState.sortValue = location.state.sortValue;
              backState.page = location.state.page;
            }

            navigate(location.state.fromPage, { state: backState });
          } else {
            // Default fallback - go back in history
            navigate(-1);
          }
        }}
      >
        ← Back
      </button>
      <h2 className="text-2xl font-bold mb-4">{displayName}</h2>
      <div className="flex flex-wrap gap-4 mb-6 items-center">
        <div>
          <label className="mr-2 font-medium">Start Date:</label>
          <DatePicker
            selected={startDate}
            onChange={setStartDate}
            dateFormat="yyyy-MM-dd"
            maxDate={endDate}
            className="input input-bordered"
          />
        </div>
        <div>
          <label className="mr-2 font-medium">End Date:</label>
          <DatePicker
            selected={endDate}
            onChange={setEndDate}
            dateFormat="yyyy-MM-dd"
            minDate={startDate}
            maxDate={new Date()}
            className="input input-bordered"
          />
        </div>
        <div className="form-control">
          <label className="label cursor-pointer">
            <span className="label-text mr-2">Unit Sold</span>
            <input
              type="checkbox"
              className="toggle toggle-primary"
              checked={showQuantity}
              onChange={(e) => setShowQuantity(e.target.checked)}
            />
          </label>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : error ? (
        <div className="text-red-500">{error}</div>
      ) : data ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Indian Map */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              State-wise Sales Distribution (
              {showQuantity ? "Quantity" : "Amount"})
            </h3>
            <div className="h-[400px]" style={{ position: "relative" }}>
              <DatamapsIndia
                style={{ position: "relative", left: "25%" }}
                regionData={mapData}
                hoverComponent={({ value }) => (
                  <div>
                    <div>
                      <p className="font-semibold">{value.name}</p>
                      <p className="text-sm">
                        {showQuantity ? "Quantity: " : "Sales: "}
                        {showQuantity
                          ? value.value ?? 0
                          : formatAmount(value.value ?? 0)}
                      </p>
                    </div>
                  </div>
                )}
                mapLayout={{
                  title: "",
                  legendTitle: "",
                  startColor: "#e0f2fe",
                  endColor: "#0ea5e9",
                  hoverTitle: "Count",
                  noDataColor: "#e0f2fe",
                }}
              />
            </div>
          </div>
          {/* Sales Line Graph */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              Sales Trend ({showQuantity ? "Quantity" : "Amount"})
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={salesLineData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="date"
                    tickFormatter={(date) => {
                      if (daysDifference > 30) {
                        // For monthly data, just return the month string
                        return date;
                      } else {
                        // For daily data, format as day + month
                        const d = new Date(date);
                        return `${d
                          .getDate()
                          .toString()
                          .padStart(2, "0")} ${d.toLocaleString("default", {
                          month: "short",
                        })}`;
                      }
                    }}
                    // tickSize={8}
                    interval="preserveStartEnd"
                    angle={-45}
                    dy={10}
                  />
                  <YAxis 
                    tick={{ fontSize: 14, fill: "#6b7280" }}
                    tickFormatter={(value) => showQuantity ? `${value}` : formatAmount(value)}
                  />
                  <Tooltip
                    formatter={(value) => [
                      showQuantity ? `${value} units` : formatAmount(value),
                      showQuantity ? "Quantity" : "Amount",
                    ]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  />
                  <Line
                    type="natural"
                    dataKey="value"
                    stroke="#0ea5e9"
                    dot={false}
                  >
                    {shouldShowSalesLabels && (
                      <LabelList 
                        dataKey="value" 
                        position="top" 
                        formatter={(value, name, props) => {
                          const index = props.index;
                          return showQuantity ? `${value} units` : formatAmount(value);
                        }}
                        content={(props) => {
                          const { x, y, value, index } = props;
                          if (!value) return null;
                          
                          // Alternate between top and bottom positions
                          const isEven = index % 2 === 0;
                          const offset = isEven ? -15 : 15;
                          
                          return (
                            <text
                              x={x}
                              y={y + offset}
                              textAnchor="middle"
                              dominantBaseline={isEven ? "baseline" : "hanging"}
                              fill="#374151"
                              fontSize="12"
                              fontWeight="bold"
                            >
                              {showQuantity ? `${value} units` : formatAmount(value)}
                            </text>
                          );
                        }}
                      />
                    )}
                  </Line>
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Zone Sales Bar Graph */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              Zone Sales ({showQuantity ? "Quantity" : "Amount"})
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={zoneBarData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 12, fill: "#6b7280" }}
                  />
                  <YAxis 
                    tick={{ fontSize: 14, fill: "#6b7280" }}
                    tickFormatter={(value) => showQuantity ? `${value}` : formatAmount(value)}
                  />
                  <Tooltip
                    formatter={(value) => [
                      showQuantity ? `${value} units` : formatAmount(value),
                      showQuantity ? "Quantity" : "Amount",
                    ]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                    {zoneBarData.map((entry, index) => (
                      <Cell
                        key={`cell-zone-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                    {shouldShowZoneLabels && (
                      <LabelList 
                        dataKey="value" 
                        position="center" 
                        formatter={(value) => showQuantity ? `${value} units` : formatAmount(value)}
                        content={(props) => {
                          const { x, y, width, height, value } = props;
                          if (!value) return null;
                          
                          // Check if bar is too small to show label inside (less than 30px width)
                          const isBarTooSmall = (width || 0) < 30;
                          
                          let labelX, labelY;
                          
                          if (isBarTooSmall) {
                            // Position label on top of the bar when bar is too small
                            labelX = (x || 0) + (width || 0) / 2; // Center of the bar
                            labelY = y - 12; // Further above the bar to prevent overlap
                          } else {
                            // Position label inside the bar when bar is large enough
                            labelX = (x || 0) + (width || 0) / 2; // Center of the bar
                            labelY = y + (height || 0) / 2; // Inside the bar
                          }
                          
                          return (
                            <text
                              x={labelX}
                              y={labelY}
                              fill={isBarTooSmall ? "black" : "white"}
                              fontSize="12"
                              fontWeight="bold"
                              textAnchor="middle"
                              dominantBaseline={isBarTooSmall ? "baseline" : "middle"}
                              transform={isBarTooSmall ? "" : `rotate(-90, ${labelX}, ${labelY})`}
                            >
                              {showQuantity ? `${value} units` : formatAmount(value)}
                            </text>
                          );
                        }}
                      />
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          {/* Platform Sales Bar Graph */}
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">
              Platform Sales ({showQuantity ? "Quantity" : "Amount"})
            </h3>
            <div className="h-[400px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart 
                  data={platformBarData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis 
                    tick={{ fontSize: 14, fill: "#6b7280" }}
                    tickFormatter={(value) => showQuantity ? `${value}` : formatAmount(value)}
                  />
                  <Tooltip
                    formatter={(value) => [
                      showQuantity ? `${value} units` : formatAmount(value),
                      showQuantity ? "Quantity" : "Amount",
                    ]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                    }}
                  />
                  <Bar dataKey="value" radius={[4, 4, 0, 0]} barSize={40}>
                    {platformBarData.map((entry, index) => (
                      <Cell
                        key={`cell-platform-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                    {shouldShowPlatformLabels && (
                      <LabelList 
                        dataKey="value" 
                        position="center" 
                        formatter={(value) => showQuantity ? `${value} units` : formatAmount(value)}
                        content={(props) => {
                          const { x, y, width, height, value } = props;
                          if (!value) return null;
                          
                          // Check if bar is too small to show label inside (less than 30px width)
                          const isBarTooSmall = (width || 0) < 30;
                          
                          let labelX, labelY;
                          
                          if (isBarTooSmall) {
                            // Position label on top of the bar when bar is too small
                            labelX = (x || 0) + (width || 0) / 2; // Center of the bar
                            labelY = y - 8; // Above the bar
                          } else {
                            // Position label inside the bar when bar is large enough
                            labelX = (x || 0) + (width || 0) / 2; // Center of the bar
                            labelY = y + (height || 0) / 2; // Inside the bar
                          }
                          
                          return (
                            <text
                              x={labelX}
                              y={labelY}
                              fill={isBarTooSmall ? "#6b7280" : "white"}
                              fontSize="12"
                              fontWeight="bold"
                              textAnchor="middle"
                              dominantBaseline={isBarTooSmall ? "baseline" : "middle"}
                              transform={isBarTooSmall ? "" : `rotate(-90, ${labelX}, ${labelY})`}
                            >
                              {showQuantity ? `${value} units` : formatAmount(value)}
                            </text>
                          );
                        }}
                      />
                    )}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      ) : (
        <div className="text-gray-500">
          No data available for this product and date range.
        </div>
      )}
    </div>
  );
};

export default ProductDetail;
